import { DeleteOutlined, TagsOutlined } from "@ant-design/icons";
import { gql } from "@apollo/client";
import { Button, Tooltip } from "antd";
import _ from "lodash";
import React from "react";
import { useNotification } from "../../contexts/Notifications";
import {
  useGetAllComponentTagTypesByPlanQuery,
  useUpdateComponentTagsMutation,
} from "../../generated/types";
import { Project } from "../../types";
import styles from "./ConfigPlansBottomRibbon.module.css";
import { EditComponentTagsModal } from "./EditComponentTagsModal";

interface EditComponentTagsContainerProps {
  project: Project;
  planId: string | undefined;
  selectedComponents: string[];
}

export const EditComponentTagsContainer = (
  props: EditComponentTagsContainerProps,
) => {
  const [addTagsModalVisible, setAddTagsModalVisible] =
    React.useState<boolean>(false);
  const [removeTagsModalVisible, setRemoveTagsModalVisible] =
    React.useState<boolean>(false);

  const notify = useNotification();

  const { data: allComponentTagTypesRes } =
    useGetAllComponentTagTypesByPlanQuery({
      variables: {
        customer: props.project.customer,
        project: props.project.project,
        scope: props.project.scope,
        planId: props.planId,
      },
    });
  const allComponentTags =
    allComponentTagTypesRes?.componentTagTypesByPlan ?? [];

  const [updateComponentTags] = useUpdateComponentTagsMutation({
    update: (cache, { data }) => {
      cache.modify({
        fields: {
          componentTagTypesByPlan() {
            data?.updateComponents
              ? data.updateComponents.map((component) =>
                  cache.writeFragment({
                    id: `Component:${component.id}`,
                    data: component,
                    fragment: gql`
                      fragment ComponentWithTags on Component {
                        id
                        name
                        tags
                      }
                    `,
                  }),
                )
              : [];
          },
        },
      });
    },
    onCompleted: () => {
      notify("Updated component tags", "success");
    },
    onError: (error) =>
      notify(`${"Error updating component tags"} - ${error}`, "error"),
  });

  const submitTags = (
    tags: string[],
    components: { id: string; tags: string[] }[],
  ) => {
    return updateComponentTags({
      variables: {
        customer: props.project.customer,
        project: props.project.project,
        scope: props.project.scope,
        components: components.map((c) => ({
          id: c.id,
          tags: _.uniq([...tags, ...c.tags]),
        })),
      },
    });
  };

  const removeTags = (
    tags: string[],
    components: { id: string; tags: string[] }[],
  ) => {
    return updateComponentTags({
      variables: {
        customer: props.project.customer,
        project: props.project.project,
        scope: props.project.scope,
        components: components.map((c) => ({
          id: c.id,
          tags: _.difference(c.tags, tags),
        })),
      },
    });
  };

  return (
    <>
      <div>
        Tags:
        <div className={styles["tag-button-container"]}>
          <Tooltip title={"Add tags"}>
            <Button
              icon={<TagsOutlined />}
              disabled={props.planId ? false : true}
              onClick={() => {
                setAddTagsModalVisible(true);
              }}
            />
          </Tooltip>
        </div>
      </div>
      <div className={styles["tag-button-container"]}>
        <Tooltip title={"Remove tags"}>
          <Button
            icon={<DeleteOutlined />}
            disabled={props.planId ? false : true}
            onClick={() => {
              setRemoveTagsModalVisible(true);
            }}
          />
        </Tooltip>
      </div>
      <>
        <EditComponentTagsModal
          title="Add Component tags"
          actionText="adding"
          allowNewTagCreation={true}
          visible={addTagsModalVisible}
          onCancel={() => setAddTagsModalVisible(false)}
          onSubmit={(tags, components) => {
            submitTags(tags, components);
            setAddTagsModalVisible(false);
          }}
          componentIds={props.selectedComponents}
          project={props.project}
          planId={props.planId}
          allComponentTags={allComponentTags}
        />
        <EditComponentTagsModal
          title="Remove Component tags"
          actionText="removing"
          allowNewTagCreation={false}
          visible={removeTagsModalVisible}
          onCancel={() => setRemoveTagsModalVisible(false)}
          onSubmit={(tags, components) => {
            removeTags(tags, components);
            setRemoveTagsModalVisible(false);
          }}
          componentIds={props.selectedComponents}
          project={props.project}
          planId={props.planId}
          allComponentTags={allComponentTags}
        />
      </>
    </>
  );
};
